import React from 'react';
import {
  PublicProfile,
  getBlockedWithProfiles,
  unblockUser,
} from '../../../services/social/api';

/**
 * Blocked accounts (T2.12/REQ-78) — the management surface Apple's UGC rules
 * expect alongside the block action itself. Self-only: the endpoint returns the
 * signed-in user's own blocked list, and this is the ONE place a blocked
 * person's name still resolves — you must be able to see who you blocked to
 * unblock them.
 */

interface BlockedAccountsProps {
  userId: string;
}

const nameOf = (profile: PublicProfile): string => {
  const full = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
  return full || profile.nickname || 'Traveler';
};

const BlockedAccounts: React.FC<BlockedAccountsProps> = ({ userId }) => {
  const [blocked, setBlocked] = React.useState<PublicProfile[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true);
    getBlockedWithProfiles(userId)
      .then((page) => { if (!cancelled) setBlocked(page.profiles); })
      .catch(() => { if (!cancelled) setError('Could not load your blocked accounts.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const handleUnblock = async (id: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await unblockUser(id);
      // Drop it from the list on success — unblocking does NOT restore any
      // follow that the block tore down, so there is nothing else to reflect.
      setBlocked((current) => current.filter((profile) => profile.id !== id));
    } catch {
      // Leave the row; the action simply didn't take.
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="v2-settings-card" aria-labelledby="blocked-heading">
      <h2 id="blocked-heading">Blocked accounts</h2>
      <p className="v2-settings-card__hint">
        Blocked accounts can’t follow you, message you, or see your posts and trips, and you won’t see
        theirs.
      </p>

      {loading && <p className="v2-settings-card__hint">Loading…</p>}
      {!loading && error && <p className="v2-settings-card__hint">{error}</p>}
      {!loading && !error && blocked.length === 0 && (
        <p className="v2-settings-card__hint">You haven’t blocked anyone.</p>
      )}

      {!loading && !error && blocked.length > 0 && (
        <ul className="v2-blocked-list">
          {blocked.map((profile) => (
            <li key={profile.id} className="v2-blocked-row">
              <span className="v2-blocked-name">
                {nameOf(profile)}
                {profile.nickname && <span className="v2-blocked-handle"> @{profile.nickname}</span>}
              </span>
              <button
                type="button"
                className="v2-blocked-unblock"
                onClick={() => handleUnblock(profile.id)}
                disabled={busyId === profile.id}
              >
                {busyId === profile.id ? 'Unblocking…' : 'Unblock'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default BlockedAccounts;
